import { ExtractedValue } from '../models/extracted-values';
import { FieldModel } from '../models/extraction-models/extraction-models';
import { transformDate } from '../pipes/ocr-date.pipe';
import { FieldType } from '@ps-gui/shared-utils';
import { parseCurrency, sanitizeDateInput } from './Parser.utils';

interface ParseResult {
  value: ExtractedValue;
  error?: string;
}

const getErrorByType = (
  type: FieldType.DATE | FieldType.NUMBER | FieldType.TIME | FieldType.CURRENCY,
): string => {
  switch (type) {
    case FieldType.DATE:
      return 'Ungültiges Datum';
    case FieldType.NUMBER:
      return 'Ungültige Zahl';
    case FieldType.TIME:
      return 'Ungültige Uhrzeit';
    case FieldType.CURRENCY:
      return 'Ungültiger Betrag';
    default:
      return 'Ungültiger Wert';
  }
};

export const parseValue = (rawValue: string, fieldModel: FieldModel): ParseResult => {
  const { type, customTransformValue } = fieldModel;

  // custom transformation - configured via fieldmodel
  if (customTransformValue) {
    return { value: customTransformValue(rawValue) };
  }

  // typespecific parsing
  switch (type) {
    case FieldType.DATE: {
      const date = transformDate(sanitizeDateInput(rawValue));
      if (date.isValid()) {
        return { value: date.format('DD.MM.YYYY') };
      }
      break; // break and return rawvalue with error
    }

    case FieldType.TIME: {
      const matches = rawValue.match(/(?:[01]\d|2[0-3]):[0-5]\d/);
      if (matches && matches.length > 0) {
        return { value: matches[0] };
      }
      break; // break and return rawvalue with error
    }

    case FieldType.CURRENCY: {
      return { value: parseCurrency(rawValue) }; // in the worst case, we'll just return 0
    }

    case FieldType.NUMBER: {
      const parsedNumber = tryParseNumber(rawValue.trim());
      if (parsedNumber !== undefined) {
        return { value: parsedNumber };
      }
      break; // break and return rawvalue with error
    }

    case FieldType.STRING:
    case FieldType.DROPDOWN:
    case FieldType.RADIO:
    case FieldType.TEXTAREA:
      return { value: rawValue }; // just return the raw value
  }

  return { value: rawValue, error: getErrorByType(type) };
};

const tryParseNumber = (stringValue: string): number | undefined => {
  return !isNaN(Number(stringValue)) ? Number(stringValue) : undefined;
};
